import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { RiCloseLine, RiArrowDownSLine, RiUserAddLine } from "@remixicon/react";
import { COUNTRY_CODES, FlagImg } from "@/utils/countryCode";

const AddCrewModal = ({ onClose, onAdd, editingCrew }) => {
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [countryCode, setCountryCode] = useState("+1");
  const [phone, setPhone] = useState("");
  const [codeDropdownOpen, setCodeDropdownOpen] = useState(false);

  const isEditing = !!editingCrew;

  useEffect(() => {
    if (editingCrew) {
      setName(editingCrew.name || "");
      setRole(editingCrew.role || "");
      // Parse country code from phone if present
      const match = editingCrew.phone?.match(/^(\+\d{1,3})\s*(.*)/);
      if (match) {
        setCountryCode(match[1]);
        setPhone(match[2]);
      } else {
        setCountryCode("+1");
        setPhone(editingCrew.phone || "");
      }
    } else {
      setName("");
      setRole("");
      setCountryCode("+1");
      setPhone("");
    }
  }, [editingCrew]);

  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 10);
    if (digits.length === 0) return "";
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const handlePhoneChange = (e) => {
    setPhone(formatPhone(e.target.value));
  };

  const isFormValid = name.trim() && role.trim() && phone.replace(/\D/g, "").length >= 7;

  const handleSubmit = () => {
    if (!isFormValid) return;
    onAdd({
      name: name.trim(),
      role: role.trim(),
      phone: `${countryCode} ${phone}`,
    });
  };

  const selectedCountry = COUNTRY_CODES.find((c) => c.code === countryCode) || COUNTRY_CODES[0];

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md shadow-xl mx-4 mb-0">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5">
          <h2 className="text-lg font-bold text-primary-500">
            {isEditing ? "Edit Crew" : "Add Crew"}
          </h2>
          <button onClick={onClose} className="p-1 rounded-full border border-gray-200 bg-white text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors focus:outline-none">
            <RiCloseLine className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <div className="p-5 space-y-4">
          {/* Crew Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Crew Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Doe"
              className="w-full text-sm border border-gray-200 rounded-xl px-3.5 py-2.5 outline-none focus:border-primary-400 transition-colors"
            />
          </div>

          {/* Position */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Position <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder="Enter crew position"
              className="w-full text-sm border border-gray-200 rounded-xl px-3.5 py-2.5 outline-none focus:border-primary-400 transition-colors"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Phone <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-2">
              {/* Country code selector */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setCodeDropdownOpen(!codeDropdownOpen)}
                  className="flex items-center gap-1.5 border border-gray-200 rounded-xl px-3 py-2.5 text-sm hover:bg-gray-50 transition-colors min-w-[90px]"
                >
                  <FlagImg iso={selectedCountry.iso} />
                  <span className="text-gray-700">{selectedCountry.code}</span>
                  <RiArrowDownSLine className="w-3.5 h-3.5 text-gray-400" />
                </button>
                {codeDropdownOpen && (
                  <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-10 max-h-48 overflow-y-auto w-36">
                    {COUNTRY_CODES.map((c) => (
                      <button
                        key={c.code}
                        type="button"
                        onClick={() => {
                          setCountryCode(c.code);
                          setCodeDropdownOpen(false);
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                          c.code === countryCode ? "bg-primary-50 text-primary-600" : "text-gray-700"
                        }`}
                      >
                        <FlagImg iso={c.iso} />
                        <span>{c.code}</span>
                        <span className="text-gray-400 text-xs">{c.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {/* Phone input */}
              <input
                type="tel"
                value={phone}
                onChange={handlePhoneChange}
                placeholder="(555) 000-0000"
                className="flex-1 text-sm border border-gray-200 rounded-xl px-3.5 py-2.5 outline-none focus:border-primary-400 transition-colors"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 pt-0">
          <button
            onClick={handleSubmit}
            disabled={!isFormValid}
            className={`w-full flex items-center justify-center py-3 text-sm rounded-full transition-colors ${
              isFormValid
                ? "bg-primary-500 text-white"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            }`}
          >
            <RiUserAddLine className="w-4 h-4 mr-2" />
            {isEditing ? "Save Changes" : "Add Crew Member"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddCrewModal;
